import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAppDispatch } from "../../redux/hooks";
import { setActiveTab } from "../../redux/slice/activeTabSlice";
import { setSelectedLeague } from "../../redux/slice/selectedLeagueSlice";
import { Api } from "../../services/interceptor";
import { QUERY_KEY } from "../../services/queryEndPoint";
import useDebounce from "../../hooks/useDebounce";
import { Images } from "../../utils/imageConstants";
import { SearchAvatar } from "./SearchResultContent";
import {
  extractGlobalSearchItems,
  buildMinimalLeagueFromWebSearch,
  type GlobalSearchItem,
} from "../../utils/websearchParse";

// ─── Component ─────────────────────────────────────────────────────────────────

type GlobalSearchBarProps = {
  placeholder?: string;
  className?: string;
};

const GlobalSearchBar: React.FC<GlobalSearchBarProps> = ({
  placeholder = "Search leagues, tournaments, teams, players...",
  className = "",
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const debouncedTerm = useDebounce(searchTerm, 350);

  const { isLoading, data: searchResults = [] } = useQuery<GlobalSearchItem[]>({
    queryKey: [QUERY_KEY.websearch, "global-bar", debouncedTerm],
    enabled: isOpen && debouncedTerm.trim().length > 0,
    queryFn: async () => {
      const response = await Api.get(QUERY_KEY.websearch, {
        params: { name: debouncedTerm.trim() },
      });
      return extractGlobalSearchItems(response?.data);
    },
  });

  const close = () => {
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleSelect = (item: GlobalSearchItem) => {
    const leagueId = item.leagueId ?? (item.type === "league" ? item.id : undefined);
    const leagueName = item.leagueName ?? (item.type === "league" ? item.title : undefined);

    if (leagueId && leagueName) {
      dispatch(setSelectedLeague(buildMinimalLeagueFromWebSearch(leagueId, leagueName)));
    }

    if (item.type === "league") {
      dispatch(setActiveTab("home"));
      navigate(leagueId ? `/my-leagues/${leagueId}` : "/leagues-home");
      close();
      return;
    }

    if (item.type === "tournament" && item.tournamentId) {
      sessionStorage.setItem("tournamentId", item.tournamentId);
      sessionStorage.setItem("tournamentName", item.tournamentName ?? item.title);
      if (leagueId) {
        navigate(`/my-leagues/${leagueId}/tournaments/${item.tournamentId}`);
      } else {
        navigate("/tournaments/tournaments-details");
      }
      close();
      return;
    }

    if (item.type === "team") {
      if (item.teamId) {
        sessionStorage.setItem("teamId", item.teamId);
        sessionStorage.setItem("teamSnapshotId", item.teamSnapshotId ?? "");
        sessionStorage.setItem("tournamentId", item.tournamentId ?? "");
        navigate("/tournaments/team-info");
      }
      close();
      return;
    }

    if (item.type === "player") {
      const pid = item.playerId ?? item.id;
      if (!pid) { close(); return; }
      if (item.teamId) sessionStorage.setItem("teamId", item.teamId);
      if (item.tournamentId) sessionStorage.setItem("tournamentId", item.tournamentId);
      sessionStorage.setItem("playerId", pid);
      navigate(`/tournaments/player-profile?playerId=${encodeURIComponent(pid)}`);
      close();
      return;
    }
  };

  // Close on outside click / Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) close();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  // Focus input when popover opens
  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  return (
    <div className={`global-search-bar ${className}`} ref={wrapperRef}>
      <div className="global-search-bar__input-wrap">
        <img src={Images.dark_search_icon} alt="Search" className="global-search-bar__icon" />
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="global-search-bar__input"
        />
      </div>

      {isOpen && (
        <div className="global-search-bar__dropdown" role="listbox">
          {debouncedTerm.trim().length === 0 ? null
            : isLoading ? (
              <div className="global-search-bar__empty">Searching…</div>
            ) : searchResults.length > 0 ? (
              <ul className="global-search-bar__list">
                {searchResults.map((item) => (
                  <li
                    key={`${item.type}-${item.id}`}
                    className="global-search-bar__item"
                    role="option"
                    onClick={(e) => { e.stopPropagation(); handleSelect(item); }}
                  >
                    <span className="sr-player-row">
                      <SearchAvatar item={item} />
                      <span className="sr-player-body">
                        <span className="global-search-bar__item-title">{item.title}</span>
                        {(item.type === "player" ? item.playerCode ?? item.subtitle : item.subtitle) && (
                          <span className="global-search-bar__item-sub">{item.type === "player" ? item.playerCode ?? item.subtitle : item.subtitle}</span>
                        )}
                      </span>
                    </span>
                    <span className={`global-search-bar__item-type type-${item.type}`}>{item.type}</span>
                  </li>
                ))}
              </ul>
            ) : debouncedTerm.trim().length > 0 ? (
              <div className="global-search-bar__empty">No results found</div>
            ) : null}
        </div>
      )}
    </div>
  );
};

export default GlobalSearchBar;
